/* eslint-disable jsx-a11y/anchor-is-valid */
import {useEffect, useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {Link, useLocation, useSearchParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {useIntl} from 'react-intl'
import {getUserByToken, login, resendVerificationEmail} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {TURNSTILE_FAILURE_MESSAGE, useTurnstile} from '../core/useTurnstile'
import {Toast} from '../../../../_metronic/partials/widgets'
import {AuthAuroraShell, AuthBrandHeader} from './AuthAurora'
import '../../../../_metronic/assets/css/Login.css'

const initialValues = {
  email: '',
  password: '',
}

export function Login() {
  const intl = useIntl()
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [emailNotVerified, setEmailNotVerified] = useState(false)
  const [resendingEmail, setResendingEmail] = useState(false)
  const {saveAuth, setCurrentUser, auth, logout} = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()
  const successMessage = (location.state as {successMessage?: string} | null)?.successMessage
  const {containerRef: turnstileRef, getToken: getCaptchaToken} = useTurnstile('login')

  // Check for email verification results
  useEffect(() => {
    const verified = searchParams.get('verified')
    const error = searchParams.get('error')

    if (verified === '1') {
      if (auth?.api_token) {
        logout()
      }

      Toast.fire({
        icon: 'success',
        title: 'Email verificado com sucesso!',
        text: 'Sua conta foi ativada. Agora você pode fazer login.',
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })

      // Remove verified parameter from URL
      searchParams.delete('verified')
      setSearchParams(searchParams, {replace: true})
    }

    // Handle errors
    if (error) {
      let toastConfig: {
        icon: 'error'
        title: string
        text: string
        customClass: {
          popup: string
          title: string
        }
      } = {
        icon: 'error',
        title: 'Erro na verificação',
        text: '',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      }

      switch (error) {
        case 'method_not_allowed':
          toastConfig.title = 'Método não permitido'
          toastConfig.text =
            'Esta operação só pode ser realizada através do link enviado por email.'
          break
        case 'invalid_parameters':
          toastConfig.title = 'Parâmetros inválidos'
          toastConfig.text =
            'O link de verificação está incompleto ou inválido. Por favor, use o link completo enviado por email.'
          break
        case 'user_not_found':
          toastConfig.title = 'E-mail verificado'
          toastConfig.text = 'O e-mail já foi verificado anteriormente.'
          break
        case 'invalid_token':
          toastConfig.title = 'Link expirado ou inválido'
          toastConfig.text =
            'Este link de verificação expirou ou já foi utilizado. Solicite um novo link de verificação.'
          break
        case 'verification_failed':
          toastConfig.title = 'Erro ao verificar email'
          toastConfig.text =
            'Ocorreu um erro ao processar a verificação. Por favor, tente novamente mais tarde ou solicite um novo link.'
          break
        default:
          toastConfig.title = 'Erro na verificação'
          toastConfig.text = 'Ocorreu um erro inesperado. Por favor, tente novamente.'
      }

      Toast.fire(toastConfig)

      // Remove error parameter from URL
      searchParams.delete('error')
      setSearchParams(searchParams, {replace: true})
    }
  }, [searchParams, auth, logout, setSearchParams])

  // 'Wrong email format'
  const loginSchema = Yup.object().shape({
    email: Yup.string()
      .email(intl.formatMessage({id: 'FORM.INPUT.VALIDATION.EMAIL'}))
      .max(50, intl.formatMessage({id: 'FORM.INPUT.VALIDATION.MAX_LENGTH'}, {max: '50'}))
      .required(intl.formatMessage({id: 'FORM.INPUT.VALIDATION.REQUIRED'})),
    password: Yup.string()
      .min(8, intl.formatMessage({id: 'FORM.INPUT.VALIDATION.MIN_LENGTH'}, {min: '8'}))
      .max(50, intl.formatMessage({id: 'FORM.INPUT.VALIDATION.MAX_LENGTH'}, {max: '50'}))
      .required(intl.formatMessage({id: 'FORM.INPUT.VALIDATION.REQUIRED'})),
  })

  const handleResendVerificationEmail = async () => {
    if (!formik.values.email) {
      Toast.fire({
        icon: 'error',
        title: 'Erro',
        text: 'Por favor, informe seu email no campo acima.',
      })
      return
    }

    setResendingEmail(true)
    try {
      await resendVerificationEmail(formik.values.email)
      Toast.fire({
        icon: 'success',
        title: 'Email de verificação reenviado!',
        customClass: {
          popup: 'bg-light-success',
          title: 'text-success',
        },
      })
      setEmailNotVerified(false)
    } catch (error: any) {
      Toast.fire({
        icon: 'error',
        title: 'Erro',
        text:
          error?.response?.data?.message || 'Erro ao reenviar email. Tente novamente mais tarde.',
        customClass: {
          popup: 'bg-light-danger',
          title: 'text-danger',
        },
      })
    } finally {
      setResendingEmail(false)
    }
  }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setEmailNotVerified(false)
      setLoading(true)

      let captchaToken: string
      try {
        captchaToken = await getCaptchaToken()
      } catch {
        setStatus(TURNSTILE_FAILURE_MESSAGE)
        setSubmitting(false)
        setLoading(false)
        return
      }

      try {
        const {data: auth} = await login(values.email, values.password, captchaToken)
        saveAuth(auth)
        const {data: user} = await getUserByToken(auth.api_token)
        setCurrentUser(user)
      } catch (error: any) {
        console.error(error)
        saveAuth(undefined)

        // Check if error is due to unverified email
        if (error?.response?.status === 403 && error?.response?.data?.email_verified === false) {
          setEmailNotVerified(true)
          setStatus('')
        } else if (error?.response?.status === 429) {
          setEmailNotVerified(false)
          setStatus(
            error?.response?.data?.message ||
              'Muitas tentativas de login. Aguarde um minuto e tente novamente.'
          )
        } else {
          setEmailNotVerified(false)
          setStatus('Os detalhes de login estão incorretos')
        }

        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <AuthAuroraShell>
      <AuthBrandHeader tagline={intl.formatMessage({id: 'AUTH.LOGIN.TAGLINE'})} />

      {/*begin::Form*/}
      <form
        className='form w-100'
        onSubmit={formik.handleSubmit}
        noValidate
        id='kt_login_signin_form'
      >
        {/*begin::Heading*/}
        <div className='text-center mb-8'>
          {/*begin::Title*/}
          <h1 className='text-dark fw-bolder mb-3'>
            {intl.formatMessage({id: 'AUTH.LOGIN.TITLE'})}
          </h1>
          {/*end::Title*/}

          {/*begin::Subtitle*/}
          <div className='text-gray-500 fw-semibold fs-6'>
            {intl.formatMessage({id: 'AUTH.LOGIN.CREDENTIALS'})}
          </div>
          {/*end::Subtitle*/}
        </div>
        {/*end::Heading*/}

        {successMessage && (
          <div className='mb-lg-15 alert alert-success'>
            <div className='alert-text font-weight-bold'>{successMessage}</div>
          </div>
        )}
        {emailNotVerified && (
          <div className='mb-lg-15 alert alert-warning'>
            <div className='alert-text'>
              <span>Por favor, verifique seu email antes de fazer login. </span>
              <button
                type='button'
                className='btn btn-link p-0 text-warning text-decoration-underline'
                onClick={handleResendVerificationEmail}
                disabled={resendingEmail}
              >
                {resendingEmail ? 'Enviando...' : 'Reenviar email de verificação'}
              </button>
            </div>
          </div>
        )}
        {formik.status && !emailNotVerified && (
          <div className='mb-lg-15 alert alert-danger'>
            <div className='alert-text font-weight-bold'>{formik.status}</div>
          </div>
        )}
        {/* begin::Form group */}
        <div className='fv-row mb-8'>
          {/* <label className='form-label fs-6 text-dark'>
                      {intl.formatMessage({id: 'AUTH.INPUT.EMAIL'})}
                    </label> */}
          <input
            placeholder={intl.formatMessage({id: 'AUTH.INPUT.EMAIL'})}
            {...formik.getFieldProps('email')}
            onChange={(e) => {
              formik.handleChange(e)
              if (emailNotVerified) {
                setEmailNotVerified(false)
              }
            }}
            className={clsx(
              'form-control bg-transparent',
              {'is-invalid': formik.touched.email && formik.errors.email},
              {
                'is-valid': formik.touched.email && !formik.errors.email,
              }
            )}
            type='email'
            name='email'
            autoComplete='off'
          />
          {formik.touched.email && formik.errors.email && (
            <div className='fv-plugins-message-container invalid-feedback'>
              <span role='alert'>{formik.errors.email}</span>
            </div>
          )}
        </div>
        {/* end::Form group */}

        <div className='fv-row mb-3'>
          {/* <label className='form-label text-dark fs-6 mb-0'>
                      {intl.formatMessage({id: 'AUTH.INPUT.PASSWORD'})}
                    </label> */}
          <div className='password-field-wrapper'>
            <input
              {...formik.getFieldProps('password')}
              placeholder={intl.formatMessage({id: 'AUTH.INPUT.PASSWORD'})}
              type={showPassword ? 'text' : 'password'}
              name='password'
              autoComplete='off'
              className={clsx(
                'form-control bg-transparent',
                {
                  'is-invalid': formik.touched.password && formik.errors.password,
                },
                {
                  'is-valid': formik.touched.password && !formik.errors.password,
                }
              )}
            />
            <button
              type='button'
              className='btn-icon'
              onClick={() => setShowPassword(!showPassword)}
            >
              <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`}></i>
            </button>
          </div>
          {formik.touched.password && formik.errors.password && (
            <div className='fv-plugins-message-container'>
              <div className='fv-help-block'>
                <span role='alert'>{formik.errors.password}</span>
              </div>
            </div>
          )}
        </div>

        {/*begin::Wrapper*/}
        <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
          <div />

          {/* begin::Link */}
          <Link to='/auth/forgot-password' className='link-primary'>
            {intl.formatMessage({id: 'AUTH.FORGOT.TITLE'})}
          </Link>
          {/* end::Link */}
        </div>
        {/*end::Wrapper*/}

        {/* Turnstile: só aparece quando a Cloudflare pede interação */}
        <div ref={turnstileRef} className='d-flex justify-content-center mb-4' />

        {/*begin::Submit button*/}
        <div className='d-grid mb-10'>
          <button
            type='submit'
            id='kt_sign_in_submit'
            className='btn btn-primary'
            disabled={formik.isSubmitting || !formik.isValid}
          >
            {!loading && (
              <span className='indicator-label'>
                {intl.formatMessage({id: 'AUTH.LOGIN.BUTTON'})}
              </span>
            )}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                {intl.formatMessage({id: 'AUTH.GENERAL.LOADING'})}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/*end::Submit button*/}

        {/*begin::Sign up link*/}
        <div className='text-center mb-3'>
          <span className='text-gray-500 fs-6'>
            Ainda não tem conta?{' '}
            <a
              href='https://taskrush.com.br/#divplanos'
              target='_blank'
              rel='noopener noreferrer'
              className='link-primary fw-semibold'
            >
              Cadastre-se agora
            </a>
          </span>
        </div>
        {/*end::Sign up link*/}

        {/*begin::Client Portal link*/}
        <div className='text-center'>
          <Link to='/auth/area-do-cliente/login' className='link-primary fw-semibold fs-6'>
            <i className='bi bi-person-circle me-2'></i>
            Sou Cliente
          </Link>
        </div>
        {/*end::Client Portal link*/}
      </form>
      {/*end::Form*/}
    </AuthAuroraShell>
  )
}
